/**
 * ComponentLoader Component
 *
 * Inline loading spinner for component-level loading states.
 * Does not block user interaction with other parts of the page.
 *
 * Features:
 * - Inline spinner (doesn't overlay)
 * - Multiple size variants
 * - Optional message
 * - Customizable alignment
 *
 * @example
 * ```tsx
 * import { ComponentLoader } from '@/components/ui/Loading';
 *
 * function MyComponent() {
 *   const { data, isLoading } = useQuery();
 *
 *   if (isLoading) {
 *     return <ComponentLoader message="Loading data..." />;
 *   }
 *
 *   return <div>{data}</div>;
 * }
 * ```
 */

import { cn } from '@/lib/utils'

interface ComponentLoaderProps {
  /** Optional message to display next to the spinner */
  message?: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Alignment of the loader */
  align?: 'left' | 'center' | 'right'
  /** Custom className for additional styling */
  className?: string
}

const sizeMap = {
  sm: 'w-4 h-4 border-2',
  md: 'w-8 h-8 border-[3px]',
  lg: 'w-12 h-12 border-4',
}

const alignMap = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
}

export function ComponentLoader({
  message,
  size = 'md',
  align = 'center',
  className = '',
}: ComponentLoaderProps) {
  return (
    <div className={cn('flex items-center gap-3', alignMap[align], className)}>
      {/* Spinner */}
      <div
        className={cn(
          'border-primary border-t-transparent rounded-full animate-spin',
          sizeMap[size]
        )}
      />

      {/* Optional message */}
      {message && (
        <p className="text-muted-foreground text-sm font-medium">{message}</p>
      )}
    </div>
  )
}

export default ComponentLoader
